import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./WorkoutMuscleSplit.module.scss";
import {
  computeMuscleSplit,
  ISplitExerciseInput,
} from "../utils/computeMuscleSplit";
import { computeTotalVolume } from "../utils/workoutVolume";
import { MuscleGroupCategory } from "../models/muscleGroups";
import { Icon, IconType } from "../../../icons/Icon";

interface IWorkoutMuscleSplitProps {
  exercises: ISplitExerciseInput[];
}

const COLLAPSED_COUNT = 3;

/** "1 000 кг" with a Bulgarian-grouped number, or null when there's no volume yet. */
const formatVolume = (kg: number): string | null =>
  kg > 0 ? `${Math.round(kg).toLocaleString("bg-BG")} кг` : null;

/**
 * Per-workout "muscle split": the workout's exercises rolled up into a percentage
 * breakdown across the high-level muscle categories, weighted by working-set count
 * (à la Hevy) so bodyweight work counts too. Shows the top few by default with a
 * "show more / less" toggle. Renders nothing until there's at least one set to measure.
 */
export const WorkoutMuscleSplit = ({ exercises }: IWorkoutMuscleSplitProps) => {
  const slices = useMemo(() => computeMuscleSplit(exercises), [exercises]);
  const [expanded, setExpanded] = useState(false);

  if (slices.length === 0) return null;

  // The "Други" catch-all is always pinned at the bottom and stays visible even
  // when the rest of the list is collapsed — it's the untracked share, so hiding
  // it would be misleading.
  const otherSlice =
    slices.find((s) => s.category === MuscleGroupCategory.Other) ?? null;
  const mainSlices = otherSlice
    ? slices.filter((s) => s !== otherSlice)
    : slices;

  const visibleMain = expanded
    ? mainSlices
    : mainSlices.slice(0, COLLAPSED_COUNT);
  const visible = otherSlice ? [...visibleMain, otherSlice] : visibleMain;
  const hiddenCount = mainSlices.length - COLLAPSED_COUNT;

  const volumeLabel = formatVolume(computeTotalVolume(exercises));

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.title}>Разпределение по мускули</div>
        {volumeLabel && (
          <div className={styles.volume} title="Общ обем (кг × повторения)">
            <Icon
              type={IconType.Dumbell}
              width={14}
              height={14}
              color="rgba(160, 158, 220, 0.95)"
            />
            <span className={styles.volume_value}>{volumeLabel}</span>
          </div>
        )}
      </div>

      <div className={styles.bars}>
        <AnimatePresence initial={false}>
          {visible.map((slice, index) => (
            <motion.div
              key={slice.category}
              className={styles.row}
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{
                opacity: 1,
                height: "auto",
                // Spacing lives on the row (not a container gap) so it collapses
                // in lockstep with the height when the row animates out.
                marginTop: index === 0 ? 0 : 10,
              }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{ duration: 0.28, ease: "easeInOut" }}
              style={{ overflow: "hidden" }}
            >
              <div className={styles.label_row}>
                <span className={styles.label}>{slice.label}</span>
                <span className={styles.percent}>{slice.percent}%</span>
              </div>
              <div className={styles.track}>
                <motion.div
                  className={styles.fill}
                  style={{ background: slice.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${slice.percent}%` }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {mainSlices.length > COLLAPSED_COUNT && (
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Покажи по-малко" : `Покажи още ${hiddenCount}`}
        </button>
      )}
    </div>
  );
};
